// SPDX-License-Identifier: AGPL-3.0-or-later

// Live renderers for the built-in animated cosmetics (catalog entries carrying an `fx` key).
// Each canvas draws one still frame when idle and only animates while `active`, the tab is
// visible and the user has not asked for reduced motion.

import {DECO_AR, DECO_C, DECORATION_FX} from '@app/features/cosmetics/fx/DecorationFx';
import {NAMEPLATE_FX} from '@app/features/cosmetics/fx/NameplateFx';
import {useGamePerformanceActive} from '@app/features/performance/state/GamePerformanceMode';
import {useEffect, useRef} from 'react';

type Frame = (t: number, dt: number) => void;

const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Drives `frame` with rAF while active; otherwise renders a single still frame. */
function useFxLoop(activeProp: boolean, frameRef: React.MutableRefObject<Frame | null>, deps: ReadonlyArray<unknown>) {
	const paused = useGamePerformanceActive();
	const active = activeProp && !paused;
	useEffect(() => {
		let raf = 0;
		let last = performance.now();
		let t = 3;
		const run: Frame = (time, dt) => {
			try {
				frameRef.current?.(time, dt);
			} catch {
				frameRef.current = null; // stop drawing this item instead of crashing the page
			}
		};
		run(t, 0);
		if (!active || reduceMotion) return;
		const loop = (now: number) => {
			raf = requestAnimationFrame(loop);
			if (document.hidden) {
				last = now;
				return;
			}
			const dt = Math.min(0.05, (now - last) / 1000);
			last = now;
			t += dt;
			run(t, dt);
		};
		raf = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(raf);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, ...deps]);
}

export function hasNameplateFx(key: string | null | undefined): key is string {
	return Boolean(key && NAMEPLATE_FX[key]);
}

export function hasDecorationFx(key: string | null | undefined): key is string {
	return Boolean(key && DECORATION_FX[key]);
}

// Nameplate scenes are authored at 2× and sample their own pixels (reflections), so the
// backing store is always 2× the CSS size.
const PLATE_DPR = 2;

export function NameplateFxCanvas({fxKey, active, className}: {fxKey: string; active: boolean; className?: string}) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const frameRef = useRef<Frame | null>(null);
	const sizeRef = useRef({w: 0, h: 0, state: {} as Record<string, unknown>});

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const plate = NAMEPLATE_FX[fxKey];
		const g = canvas.getContext('2d');
		if (!plate || !g) return;
		const resize = () => {
			const rect = canvas.getBoundingClientRect();
			if (rect.width < 2 || rect.height < 2) return;
			canvas.width = Math.round(rect.width * PLATE_DPR);
			canvas.height = Math.round(rect.height * PLATE_DPR);
			const state = {};
			plate.build(rect.width, rect.height, state);
			sizeRef.current = {w: rect.width, h: rect.height, state};
			frameRef.current?.(3, 0);
		};
		frameRef.current = (t) => {
			const {w, h, state} = sizeRef.current;
			if (!w) return;
			g.setTransform(PLATE_DPR, 0, 0, PLATE_DPR, 0, 0);
			g.clearRect(0, 0, w, h);
			plate.draw(g, w, h, t, state);
		};
		resize();
		const observer = new ResizeObserver(resize);
		observer.observe(canvas);
		return () => {
			observer.disconnect();
			frameRef.current = null;
			// Free the 2x backing store now; long lists mount and unmount many plates.
			canvas.width = 0;
			canvas.height = 0;
		};
	}, [fxKey]);

	useFxLoop(active, frameRef, [fxKey]);
	return (
		<canvas
			ref={canvasRef}
			className={className}
			aria-hidden
			style={{display: 'block', width: '100%', height: '100%'}}
		/>
	);
}

// Decorations are authored around an avatar of radius DECO_AR centred at DECO_C. The overlay box
// is 1.3× the avatar; everything is clipped to 1.2× (Discord's bound).
const BOX_HALF = DECO_AR * 1.3;
const CLIP_HALF = DECO_AR * 1.2;

export function DecorationFxCanvas({fxKey, active, className}: {fxKey: string; active: boolean; className?: string}) {
	const backRef = useRef<HTMLCanvasElement>(null);
	const frontRef = useRef<HTMLCanvasElement>(null);
	const frameRef = useRef<Frame | null>(null);

	useEffect(() => {
		const back = backRef.current;
		const front = frontRef.current;
		const deco = DECORATION_FX[fxKey];
		if (!back || !front || !deco) return;
		const b = back.getContext('2d');
		const f = front.getContext('2d');
		if (!b || !f) return;
		const state: Record<string, unknown> = {hover: active};
		deco.init(state);
		let scale = 1;
		const resize = () => {
			const rect = back.getBoundingClientRect();
			if (rect.width < 2) return;
			const dpr = Math.min(2, window.devicePixelRatio || 1);
			for (const c of [back, front]) {
				c.width = Math.round(rect.width * dpr);
				c.height = Math.round(rect.height * dpr);
			}
			scale = (rect.width * dpr) / (BOX_HALF * 2);
			frameRef.current?.(3, 0);
		};
		frameRef.current = (t) => {
			for (const L of [b, f]) {
				L.setTransform(1, 0, 0, 1, 0, 0);
				L.clearRect(0, 0, back.width, back.height);
				L.setTransform(scale, 0, 0, scale, -(DECO_C - BOX_HALF) * scale, -(DECO_C - BOX_HALF) * scale);
				L.save();
				L.beginPath();
				L.rect(DECO_C - CLIP_HALF, DECO_C - CLIP_HALF, CLIP_HALF * 2, CLIP_HALF * 2);
				L.clip();
			}
			deco.draw(b, f, t, state);
			b.restore();
			f.restore();
			// The back layer sits behind the avatar: cut the avatar disc out of it.
			b.save();
			b.globalCompositeOperation = 'destination-out';
			b.beginPath();
			b.arc(DECO_C, DECO_C, DECO_AR, 0, Math.PI * 2);
			b.fill();
			b.restore();
		};
		resize();
		const observer = new ResizeObserver(resize);
		observer.observe(back);
		return () => {
			observer.disconnect();
			frameRef.current = null;
			for (const c of [back, front]) {
				c.width = 0;
				c.height = 0;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fxKey]);

	useFxLoop(active, frameRef, [fxKey]);
	const layer: React.CSSProperties = {position: 'absolute', inset: 0, width: '100%', height: '100%'};
	return (
		<div className={className} aria-hidden style={{position: 'relative', width: '100%', height: '100%'}}>
			<canvas ref={backRef} style={layer} />
			<canvas ref={frontRef} style={layer} />
		</div>
	);
}
